import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  IconButton,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import '../styles.scss';
import './styles.scss';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import useScreenWidth from '~/hooks/useScreenWidth';
import AddCostOfGoodModal from './AddCostOfGoodModal';
import EditCostOfGoodModal from './EditCostOfGoodModal';
import CategoryTable from './Tables/CategoryTable';
import ConfirmModal from '~/components/General/ConfirmModal';
import {
  ContactDetailsDeleteCIcon,
  EditCampaignStepCIcon,
  TrashTemplateCIcon,
} from '~/assets/images';
import {
  fetchAdminCostOfGoodsData,
  fetchAdminCostOfGoodsById,
  fetchAdminCostOfGoodsHistoricalData,
  createAdminCostOfGoods,
  updateAdminCostOfGoods,
  deleteAdminCostOfGoods,
} from '~/redux/actions/admin-pricing-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// Format date to YYYY-MM-DD format for API payloads
const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // If it's already a string, extract just the date part
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    // Format Date object to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

const CostOfGood = () => {
  const dispatch = useDispatch();
  const isWideScreen = useScreenWidth(1380);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const openMenu = Boolean(menuAnchor);
  const [tableLoading, setTableLoading] = useState(false);
  const [historicalTableLoading, setHistoricalTableLoading] = useState(false);

  // Pagination state management (following Orders/Postage pattern)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });
  const [historicalPagination, setHistoricalPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });

  // Redux state
  const costOfGoodsData =
    useSelector((state) => state.adminPricingReducers.costOfGoods?.current) || [];
  const historicalData =
    useSelector((state) => state.adminPricingReducers.costOfGoods?.historical) || [];
  const loading = useSelector((state) => state.adminPricingReducers.costOfGoods?.loading) || false;

  // Redux pagination data (like Orders/Postage component)
  const rowsCount = useSelector((state) => state.adminPricingReducers.costOfGoods?.rowsCount) || 0;
  const to = useSelector((state) => state.adminPricingReducers.costOfGoods?.to);
  const from = useSelector((state) => state.adminPricingReducers.costOfGoods?.from);
  const lastPage = useSelector((state) => state.adminPricingReducers.costOfGoods?.lastPage || 1);
  const currentPage = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.currentPage || 1
  );
  const totalRecords = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.totalRecordsInDB
  );
  const perPage = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.perPage || DEFAULT_PAGINATION.pageSize
  );

  // Historical Redux pagination data
  const historicalRowsCount =
    useSelector(
      (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.rowsCount
    ) || 0;
  const historicalTo = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.to
  );
  const historicalFrom = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.from
  );
  const historicalLastPage = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.lastPage || 1
  );
  const historicalCurrentPage = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.currentPage || 1
  );
  const historicalPerPage = useSelector(
    (state) =>
      state.adminPricingReducers.costOfGoods?.historicalPagination?.perPage ||
      DEFAULT_PAGINATION.pageSize
  );
  const historicalTotalRecords = useSelector(
    (state) => state.adminPricingReducers.costOfGoods?.historicalPagination?.totalRecordsInDB
  );

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [tabSwitchLoading, setTabSwitchLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const previousTabIdx = useRef(0);
  const isInitialMount = useRef(true);

  const tabs = ['Postcards', 'Letters', 'Real Penned', 'Self-Mailers', 'Snap Packs', 'Add-Ons'];
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    header: 'Delete Cost of Goods',
    message:
      'Are you sure you want to delete this cost of good? This will remove all items associated with the same Effective Date.',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });
  const [confirmOverrideModal, setConfirmOverrideModal] = useState({
    open: false,
    header: 'Override Cost of Goods Version',
    message:
      'A Cost of Goods Version already exists for the selected Effective Date. Do you want to overwrite the existing Cost of Goods Version with this new one?',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });

  // Menu handlers
  const handleOpenRowMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setActiveRow(row);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  // Helper function to get product type
  const getProductType = () => {
    const productTypeMap = {
      Postcards: 'postcards',
      Letters: 'letters',
      'Real Penned': 'real_penned',
      'Self-Mailers': 'self_mailers',
      'Snap Packs': 'snap_packs',
      'Add-Ons': 'add_ons',
    };
    return productTypeMap[tabs[activeTabIdx]] || 'letters';
  };

  // Helper function to map form data to API payload based on product type
  const mapFormDataToPayload = (formData, productType) => {
    const basePayload = {
      product_type: productType,
      effective_date: formatDateForAPI(formData.effective_date),
      notes: formData.notes || '',
      force_update: formData.force_update || false,
    };

    switch (productType) {
      case 'postcards':
        return {
          ...basePayload,
          four_by_six_postcard_cost: formData.four_by_six_postcard_cost,
          four_by_six_postcard_is_applicable: formData.four_by_six_postcard_is_applicable || false,
          four_by_six_postcard_additional_increase:
            formData.four_by_six_postcard_additional_increase,
          six_by_nine_postcard_cost: formData.six_by_nine_postcard_cost,
          six_by_nine_postcard_is_applicable: formData.six_by_nine_postcard_is_applicable || false,
          six_by_nine_postcard_additional_increase:
            formData.six_by_nine_postcard_additional_increase,
          six_by_eleven_postcard_cost: formData.six_by_eleven_postcard_cost,
          six_by_eleven_postcard_is_applicable:
            formData.six_by_eleven_postcard_is_applicable || false,
          six_by_eleven_postcard_additional_increase:
            formData.six_by_eleven_postcard_additional_increase,
          nine_by_twelve_postcard_cost: formData.nine_by_twelve_postcard_cost,
          nine_by_twelve_postcard_is_applicable:
            formData.nine_by_twelve_postcard_is_applicable || false,
          nine_by_twelve_postcard_additional_increase:
            formData.nine_by_twelve_postcard_additional_increase,
        };

      case 'letters':
        return {
          ...basePayload,
          personal_letter_cost: formData.personal_letter_cost,
          personal_letter_is_applicable: formData.personal_letter_is_applicable || false,
          personal_letter_additional_increase: formData.personal_letter_additional_increase,
          professional_letter_windowed_cost: formData.professional_letter_windowed_cost,
          professional_letter_windowed_is_applicable:
            formData.professional_letter_windowed_is_applicable || false,
          professional_letter_windowed_additional_increase:
            formData.professional_letter_windowed_additional_increase,
          professional_letter_non_windowed_cost: formData.professional_letter_non_windowed_cost,
          professional_letter_non_windowed_is_applicable:
            formData.professional_letter_non_windowed_is_applicable || false,
          professional_letter_non_windowed_additional_increase:
            formData.professional_letter_non_windowed_additional_increase,
        };

      case 'real_penned':
        return {
          ...basePayload,
          real_penned_personal_letter_cost: formData.real_penned_personal_letter_cost,
          real_penned_personal_letter_is_applicable:
            formData.real_penned_personal_letter_is_applicable || false,
          real_penned_personal_letter_additional_increase:
            formData.real_penned_personal_letter_additional_increase,
          real_penned_professional_letter_cost: formData.real_penned_professional_letter_cost,
          real_penned_professional_letter_is_applicable:
            formData.real_penned_professional_letter_is_applicable || false,
          real_penned_professional_letter_additional_increase:
            formData.real_penned_professional_letter_additional_increase,
        };

      case 'self_mailers':
        return {
          ...basePayload,
          bi_fold_self_mailer_cost: formData.bi_fold_self_mailer_cost,
          bi_fold_self_mailer_is_applicable: formData.bi_fold_self_mailer_is_applicable || false,
          bi_fold_self_mailer_additional_increase: formData.bi_fold_self_mailer_additional_increase,
          tri_fold_self_mailer_cost: formData.tri_fold_self_mailer_cost,
          tri_fold_self_mailer_is_applicable: formData.tri_fold_self_mailer_is_applicable || false,
          tri_fold_self_mailer_additional_increase:
            formData.tri_fold_self_mailer_additional_increase,
        };

      case 'snap_packs':
        return {
          ...basePayload,
          snap_pack_letter_cost: formData.snap_pack_letter_cost,
          snap_pack_letter_is_applicable: formData.snap_pack_letter_is_applicable || false,
          snap_pack_letter_additional_increase: formData.snap_pack_letter_additional_increase,
        };

      case 'add_ons':
        return {
          ...basePayload,
          google_street_view_cost: formData.google_street_view_cost,
          google_street_view_is_applicable: formData.google_street_view_is_applicable || false,
          google_street_view_additional_increase: formData.google_street_view_additional_increase,
          rapid_offer_system_cost: formData.rapid_offer_system_cost,
          rapid_offer_system_is_applicable: formData.rapid_offer_system_is_applicable || false,
          rapid_offer_system_additional_increase: formData.rapid_offer_system_additional_increase,
          professional_letter_second_page_cost: formData.professional_letter_second_page_cost,
          professional_letter_second_page_is_applicable:
            formData.professional_letter_second_page_is_applicable || false,
          professional_letter_second_page_additional_increase:
            formData.professional_letter_second_page_additional_increase,
          uv_coating_cost: formData.uv_coating_cost,
          uv_coating_is_applicable: formData.uv_coating_is_applicable || false,
          uv_coating_additional_increase: formData.uv_coating_additional_increase,
        };

      default:
        return basePayload;
    }
  };

  // Fetch data functions (following Orders/Postage pattern)
  const getAdminCostOfGoods = async (showTabLoader = false) => {
    if (showTabLoader) {
      setTabSwitchLoading(true);
      // Clear current data immediately to prevent old data flash
      // The Redux action will update with new data
    }

    const productType = getProductType();
    const payload = {
      pagination,
      product_type: productType,
    };

    try {
      setTableLoading(true);
      // Add delay before API call to ensure loader is visible
      if (showTabLoader) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      await dispatch(fetchAdminCostOfGoodsData(payload));
    } catch (error) {
      console.error('Failed to fetch cost of goods data:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Helper function to map API response to form data
  const mapApiResponseToFormData = (apiData, productType) => {
    console.log('mapApiResponseToFormData called with:', { apiData, productType });

    // Handle nested response structure - the actual data is in apiData.data
    const actualData = apiData.data || apiData;

    const baseData = {
      effective_date: actualData.effective_date,
      notes: actualData.notes || '',
      force_update: false,
    };

    console.log('Base Data:', baseData);

    // Handle the API response structure with pricings array
    if (actualData.pricings && Array.isArray(actualData.pricings)) {
      console.log('Found pricings array:', actualData.pricings);
      // Create a map of slug to pricing data for easy lookup
      const pricingMap = {};
      actualData.pricings.forEach((pricing) => {
        pricingMap[pricing.slug] = pricing;
      });

      switch (productType) {
        case 'postcards':
          const fourBySix = pricingMap['four_by_six_postcard'] || {};
          const sixByNine = pricingMap['six_by_nine_postcard'] || {};
          const sixByEleven = pricingMap['six_by_eleven_postcard'] || {};
          const nineByTwelve = pricingMap['nine_by_twelve_postcard'] || {};

          const result = {
            ...baseData,
            four_by_six_postcard_cost: fourBySix.cost || '',
            four_by_six_postcard_is_applicable: fourBySix.is_applicable || false,
            four_by_six_postcard_additional_increase: fourBySix.additional_increase || '',
            six_by_nine_postcard_cost: sixByNine.cost || '',
            six_by_nine_postcard_is_applicable: sixByNine.is_applicable || false,
            six_by_nine_postcard_additional_increase: sixByNine.additional_increase || '',
            six_by_eleven_postcard_cost: sixByEleven.cost || '',
            six_by_eleven_postcard_is_applicable: sixByEleven.is_applicable || false,
            six_by_eleven_postcard_additional_increase: sixByEleven.additional_increase || '',
            nine_by_twelve_postcard_cost: nineByTwelve.cost || '',
            nine_by_twelve_postcard_is_applicable: nineByTwelve.is_applicable || false,
            nine_by_twelve_postcard_additional_increase: nineByTwelve.additional_increase || '',
          };

          return result;

        case 'letters':
          const personalLetter = pricingMap['personal_letter'] || {};
          const professionalWindowed = pricingMap['professional_letter_windowed'] || {};
          const professionalNonWindowed = pricingMap['professional_letter_non_windowed'] || {};

          return {
            ...baseData,
            personal_letter_cost: personalLetter.cost || '',
            personal_letter_is_applicable: personalLetter.is_applicable || false,
            personal_letter_additional_increase: personalLetter.additional_increase || '',
            professional_letter_windowed_cost: professionalWindowed.cost || '',
            professional_letter_windowed_is_applicable: professionalWindowed.is_applicable || false,
            professional_letter_windowed_additional_increase:
              professionalWindowed.additional_increase || '',
            professional_letter_non_windowed_cost: professionalNonWindowed.cost || '',
            professional_letter_non_windowed_is_applicable:
              professionalNonWindowed.is_applicable || false,
            professional_letter_non_windowed_additional_increase:
              professionalNonWindowed.additional_increase || '',
          };

        case 'real_penned':
          const realPennedPersonal = pricingMap['real_penned_personal_letter'] || {};
          const realPennedProfessional = pricingMap['real_penned_professional_letter'] || {};

          return {
            ...baseData,
            real_penned_personal_letter_cost: realPennedPersonal.cost || '',
            real_penned_personal_letter_is_applicable: realPennedPersonal.is_applicable || false,
            real_penned_personal_letter_additional_increase:
              realPennedPersonal.additional_increase || '',
            real_penned_professional_letter_cost: realPennedProfessional.cost || '',
            real_penned_professional_letter_is_applicable:
              realPennedProfessional.is_applicable || false,
            real_penned_professional_letter_additional_increase:
              realPennedProfessional.additional_increase || '',
          };

        case 'self_mailers':
          const biFold = pricingMap['bi_fold_self_mailer'] || {};
          const triFold = pricingMap['tri_fold_self_mailer'] || {};

          return {
            ...baseData,
            bi_fold_self_mailer_cost: biFold.cost || '',
            bi_fold_self_mailer_is_applicable: biFold.is_applicable || false,
            bi_fold_self_mailer_additional_increase: biFold.additional_increase || '',
            tri_fold_self_mailer_cost: triFold.cost || '',
            tri_fold_self_mailer_is_applicable: triFold.is_applicable || false,
            tri_fold_self_mailer_additional_increase: triFold.additional_increase || '',
          };

        case 'snap_packs':
          const snapPack = pricingMap['snap_pack_letter'] || {};

          return {
            ...baseData,
            snap_pack_letter_cost: snapPack.cost || '',
            snap_pack_letter_is_applicable: snapPack.is_applicable || false,
            snap_pack_letter_additional_increase: snapPack.additional_increase || '',
          };

        case 'add_ons':
          const googleStreetView = pricingMap['google_street_view'] || {};
          const rapidOfferSystem = pricingMap['rapid_offer_system'] || {};
          const professionalLetterSecondPage = pricingMap['professional_letter_second_page'] || {};
          const uvCoating = pricingMap['uv_coating'] || {};

          return {
            ...baseData,
            google_street_view_cost: googleStreetView.cost || '',
            google_street_view_is_applicable: googleStreetView.is_applicable || false,
            google_street_view_additional_increase: googleStreetView.additional_increase || '',
            rapid_offer_system_cost: rapidOfferSystem.cost || '',
            rapid_offer_system_is_applicable: rapidOfferSystem.is_applicable || false,
            rapid_offer_system_additional_increase: rapidOfferSystem.additional_increase || '',
            professional_letter_second_page_cost: professionalLetterSecondPage.cost || '',
            professional_letter_second_page_is_applicable:
              professionalLetterSecondPage.is_applicable || false,
            professional_letter_second_page_additional_increase:
              professionalLetterSecondPage.additional_increase || '',
            uv_coating_cost: uvCoating.cost || '',
            uv_coating_is_applicable: uvCoating.is_applicable || false,
            uv_coating_additional_increase: uvCoating.additional_increase || '',
          };

        default:
          return baseData;
      }
    } else {
      console.log('No pricings array found or not an array');
      console.log('actualData.pricings:', actualData.pricings);
    }

    return baseData;
  };

  // Prepare edit initial values by fetching data from API
  const prepareEditInitialValues = async (row) => {
    if (!row || !row.pricing_group_id) {
      return {};
    }

    try {
      const response = await dispatch(fetchAdminCostOfGoodsById(row.pricing_group_id));

      if (response.status === 200 && response.data) {
        const productType = getProductType();

        const mappedData = mapApiResponseToFormData(response.data, productType);

        return mappedData;
      } else {
        dispatch(failure('Failed to fetch cost of goods details'));
        return {};
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to fetch cost of goods details'
        )
      );
      return {};
    }
  };

  const handleCreateCostOfGoods = async (formData, forceUpdate = false) => {
    setSaveLoading(true);
    try {
      const productType = getProductType();
      const payload = mapFormDataToPayload(formData, productType);

      // Add force_update flag if this is a forced update
      if (forceUpdate) {
        payload.force_update = true;
      }

      const response = await dispatch(createAdminCostOfGoods(payload));
      if (response.status === 422) {
        // Check if override modal should be shown
        if (response.data.data && response.data.data.show_override_modal && !forceUpdate) {
          dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            loading: false,
            handleOk: () => {
              setConfirmOverrideModal((m) => ({ ...m, loading: true }));
              // Use update function like Postage component does
              handleUpdateCostOfGoods(formData, true, id).finally(() => {
                setConfirmOverrideModal((m) => ({ ...m, loading: false }));
              });
            },
            handleClose: closeOverRideModal,
          }));
          return;
        } else {
          dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
        }
      } else if (response.status !== 200) {
        dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
      } else if (response.status === 200) {
        dispatch(success(response.data.message || 'Cost of goods created successfully'));
        await getAdminCostOfGoods();

        // Reset form values (similar to Postage component)
        // This would be handled by the modal's useEffect when it closes
        setAddOpen(false);

        // Close override modal if it was open
        if (forceUpdate) {
          closeOverRideModal();
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to create cost of goods'));
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to create cost of goods'
        )
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateCostOfGoods = async (formData, forceUpdate = false, id = null) => {
    setSaveLoading(true);
    try {
      const productType = getProductType();
      const payload = mapFormDataToPayload(formData, productType);

      // Add force_update flag if this is a forced update
      if (forceUpdate) {
        payload.force_update = true;
      }

      const response = await dispatch(
        updateAdminCostOfGoods({
          ...payload,
          id: forceUpdate ? id : activeRow.pricing_group_id,
        })
      );
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Cost of goods updated successfully'));

        if (forceUpdate) {
          // Reset form and close override modal (like Postage component)
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: false,
            loading: false,
            payloadData: null,
            handleOk: () => {},
          }));
          setAddOpen(false);
        } else {
          // Reset edit form values for normal edit
          setEditInitialValues({});
          setEditOpen(false);
        }
        setEditOpen(false);
        await getAdminCostOfGoods();
      } else if (response.status === 422) {
        // Check if override modal should be shown
        if (response.data.data && response.data.data.show_override_modal && !forceUpdate) {
          dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            loading: false,
            handleOk: () => {
              setConfirmOverrideModal((m) => ({ ...m, loading: true }));
              // Use update function like Postage component does
              handleUpdateCostOfGoods(formData, true, id).finally(() => {
                setConfirmOverrideModal((m) => ({ ...m, loading: false }));
              });
            },
            handleClose: closeOverRideModal,
          }));
          return;
        } else {
          dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to update cost of goods'));
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to update cost of goods'
        )
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteCostOfGoods = async () => {
    try {
      const response = await dispatch(deleteAdminCostOfGoods(activeRow.pricing_group_id));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Cost of goods deleted successfully'));
        await getAdminCostOfGoods();
        closeDeleteModal();
      } else {
        dispatch(failure(response?.data?.message || 'Failed to delete cost of goods'));
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to delete cost of goods'
        )
      );
    }
  };

  // Fetch historical data function (following Orders/Postage pattern)
  const getAdminCostOfGoodsHistorical = async (showTabLoader = false) => {
    const productType = getProductType();
    const payload = {
      pagination: historicalPagination,
      product_type: productType,
    };

    try {
      setHistoricalTableLoading(true);
      // Add delay for tab switching to prevent old data flash
      if (showTabLoader) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      await dispatch(fetchAdminCostOfGoodsHistoricalData(payload));
    } catch (error) {
      console.error('Failed to fetch historical cost of goods data:', error);
    } finally {
      setHistoricalTableLoading(false);
    }
  };

  // Pagination handlers (following Orders/Postage pattern)
  const updatePagination = (newPagination) => {
    setPagination(newPagination);
  };

  const updateHistoricalPagination = (newPagination) => {
    setHistoricalPagination(newPagination);
  };

  // Modal close handlers
  const closeDeleteModal = () => {
    setConfirmDeleteModal((prev) => ({ ...prev, open: false, loading: false }));
  };

  const closeOverRideModal = () => {
    setConfirmOverrideModal((prev) => ({ ...prev, open: false, loading: false }));
  };

  // Helper function to reset form values based on product type
  const resetFormValues = () => {
    const productType = getProductType();
    const fieldsConfig = getFieldsConfig(productType);

    const resetValues = {
      effective_date: null,
      notes: '',
      force_update: false,
    };

    fieldsConfig.forEach((field) => {
      resetValues[`${field.key}_cost`] = '';
      resetValues[`${field.key}_is_applicable`] = false;
      resetValues[`${field.key}_additional_increase`] = '';
    });

    return resetValues;
  };

  // Get field configuration based on product type
  const getFieldsConfig = (productType) => {
    switch (productType) {
      case 'postcards':
        return [
          { key: 'four_by_six_postcard', label: '4x6 Postcard*' },
          { key: 'six_by_nine_postcard', label: '6x9 Postcard*' },
          { key: 'six_by_eleven_postcard', label: '6x11 Postcard*' },
          { key: 'nine_by_twelve_postcard', label: '9x12 Postcard*' },
        ];
      case 'letters':
        return [
          { key: 'personal_letter', label: 'Personal Letter*' },
          { key: 'professional_letter_windowed', label: 'Professional Letter (Windowed)*' },
          { key: 'professional_letter_non_windowed', label: 'Professional Letter (Non-Windowed)*' },
        ];
      case 'real_penned':
        return [
          { key: 'real_penned_personal_letter', label: 'Real Penned Personal Letter*' },
          { key: 'real_penned_professional_letter', label: 'Real Penned Professional Letter*' },
        ];
      case 'self_mailers':
        return [
          { key: 'bi_fold_self_mailer', label: 'Bi-Fold Self Mailer*' },
          { key: 'tri_fold_self_mailer', label: 'Tri-Fold Self Mailer*' },
        ];
      case 'snap_packs':
        return [{ key: 'snap_pack_letter', label: 'Snap Pack Letter*' }];
      case 'add_ons':
        return [
          { key: 'google_street_view', label: 'Google Street View*' },
          { key: 'rapid_offer_system', label: 'Rapid Offer System*' },
          { key: 'uv_coating', label: 'UV Coating*' },
        ];
      default:
        return [];
    }
  };

  // Fetch data on component mount and when pagination changes
  useEffect(() => {
    const fetchData = async () => {
      await getAdminCostOfGoods();
    };
    fetchData();
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    const fetchHistoricalData = async () => {
      await getAdminCostOfGoodsHistorical();
    };
    fetchHistoricalData();
  }, [historicalPagination.page, historicalPagination.pageSize]);

  // Handle tab switching with loader
  useEffect(() => {
    const isTabSwitch = !isInitialMount.current && previousTabIdx.current !== activeTabIdx;

    const handleTabSwitch = async () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        // Initial load without tab loader
        await getAdminCostOfGoods(false);
        await getAdminCostOfGoodsHistorical(false);
      } else if (isTabSwitch) {
        // Tab switch with loader and delays
        setIsTabSwitching(true);
        setTabSwitchLoading(true);

        // Reset pagination to initial state when switching tabs
        setPagination({
          page: 1,
          pageSize: DEFAULT_PAGINATION.pageSize,
        });
        setHistoricalPagination({
          page: 1,
          pageSize: DEFAULT_PAGINATION.pageSize,
        });

        try {
          // Wait for both API calls to complete
          await Promise.all([getAdminCostOfGoods(true), getAdminCostOfGoodsHistorical(true)]);
        } catch (error) {
          console.error('Error during tab switch:', error);
        } finally {
          // Hide loader after API calls complete
          setTabSwitchLoading(false);
          setIsTabSwitching(false);
        }
      }
    };

    handleTabSwitch();
    previousTabIdx.current = activeTabIdx;
  }, [activeTabIdx]);

  return (
    <Container maxWidth="xxl" className="olcAdminSectionPage">
      <Box className="costOfGoodWrapper">
        <Box className="sectionHeader">
          <Box className="leftHeader">
            <Box className="costOfGoodTableHeader">
              <Typography variant="h6" className="pricingControlsSectionTitle">
                Current Cost of Goods
              </Typography>
            </Box>
            <Box className="tabs">
              {tabs.map((t, idx) => (
                <Typography
                  key={t}
                  className={`${idx === activeTabIdx ? 'active' : ''} ${tabSwitchLoading && idx === activeTabIdx ? 'loading' : ''}`}
                  onClick={() => !tabSwitchLoading && setActiveTabIdx(idx)}
                  style={{
                    cursor: tabSwitchLoading ? 'not-allowed' : 'pointer',
                    opacity: tabSwitchLoading && idx !== activeTabIdx ? 0.6 : 1,
                    pointerEvents: tabSwitchLoading ? 'none' : 'auto',
                  }}
                >
                  {t}
                </Typography>
              ))}
            </Box>
          </Box>
          <OlcButton variant="primary" className="addCostBtn" onClick={() => setAddOpen(true)}>
            Add Cost of Goods
          </OlcButton>
        </Box>
        {tabSwitchLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              position: 'relative',
            }}
          >
            <CircularProgress
              sx={{
                color: '#ed5c2f',
              }}
            />
          </Box>
        ) : (
          <CategoryTable
            category={tabs[activeTabIdx]}
            onOpenRowMenu={handleOpenRowMenu}
            isWideScreen={isWideScreen}
            activeRowId={activeRow?.id}
            menuOpen={openMenu}
            data={isTabSwitching ? [] : costOfGoodsData}
            loading={tableLoading || tabSwitchLoading}
            historicalLoading={historicalTableLoading || tabSwitchLoading}
            historicalData={isTabSwitching ? [] : historicalData}
            pagination={{
              page: currentPage,
              pageSize: perPage,
              lastPage: lastPage,
              total: rowsCount,
              from: from,
              to: to,
              totalRecords: totalRecords,
            }}
            updatePagination={updatePagination}
            historicalPagination={{
              page: historicalCurrentPage,
              pageSize: historicalPerPage,
              lastPage: historicalLastPage,
              total: historicalRowsCount,
              from: historicalFrom,
              to: historicalTo,
              totalRecords: historicalTotalRecords,
            }}
            updateHistoricalPagination={updateHistoricalPagination}
          />
        )}
      </Box>

      <OlcActionMenu
        anchorEl={menuAnchor}
        open={openMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        disableScrollLock
      >
        <MenuItem
          onClick={async () => {
            try {
              handleCloseMenu();
              setEditLoading(true);

              // First fetch and prepare the data
              const init = await prepareEditInitialValues(activeRow);
              console.log('init', init);
              // Only open modal if we have data
              if (init && Object.keys(init).length > 0) {
                setEditInitialValues(init);
                setEditOpen(true);
              } else {
                dispatch(failure('No data found for editing'));
              }
            } catch (error) {
              dispatch(failure('Failed to load cost of goods data for editing'));
            } finally {
              setEditLoading(false);
            }
          }}
        >
          <img src={EditCampaignStepCIcon} alt="edit" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDeleteModal((m) => ({
              ...m,
              open: true,
              handleOk: async () => {
                setConfirmDeleteModal((mm) => ({ ...mm, loading: true }));
                await handleDeleteCostOfGoods();
                setConfirmDeleteModal((mm) => ({ ...mm, loading: false, open: false }));
                handleCloseMenu();
              },
              handleClose: () => {
                setConfirmDeleteModal((mm) => ({ ...mm, open: false, loading: false }));
                handleCloseMenu();
              },
            }));
          }}
        >
          <img src={TrashTemplateCIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      <AddCostOfGoodModal
        open={addOpen}
        handleClose={() => setAddOpen(false)}
        onSubmit={handleCreateCostOfGoods}
        productType={getProductType()}
        category={tabs[activeTabIdx]}
        loading={saveLoading}
      />
      <EditCostOfGoodModal
        key={`edit-modal-${activeRow?.pricing_group_id || 'new'}`}
        open={editOpen}
        handleClose={() => {
          setEditOpen(false);
          setEditInitialValues({});
          setEditLoading(false);
        }}
        initialValues={editInitialValues}
        loading={saveLoading}
        onSubmit={handleUpdateCostOfGoods}
        productType={getProductType()}
        category={tabs[activeTabIdx]}
      />
      <ConfirmModal openConfirmModal={confirmDeleteModal} />
      <ConfirmModal openConfirmModal={confirmOverrideModal} />
    </Container>
  );
};

export default CostOfGood;
